import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  PlusCircleIcon,
  InboxIcon,
  MapIcon,
  CameraIcon,
  TrophyIcon,
  GlobeAltIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { Postcard } from '../types';

// Helper function to convert Firestore timestamp to Date
const convertToDate = (timestamp: unknown): Date | null => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp === 'object' && timestamp !== null && 'seconds' in timestamp) {
    return new Date((timestamp as { seconds: number }).seconds * 1000);
  }
  if (typeof timestamp === 'object' && timestamp !== null && 'toDate' in timestamp && typeof (timestamp as { toDate: () => Date }).toDate === 'function') {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  return new Date(String(timestamp));
};

export function DashboardPage() {
  const { userProfile } = useAuth();
  const [recentPostcards, setRecentPostcards] = useState<Postcard[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading recent postcards with full Postcard shape
    setTimeout(() => {
      const now = Date.now();
      const mock: Postcard[] = [
        {
          id: '1',
          imageURL: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400',
          message: 'Amazing sunset in Bali! 🌅',
          translatedMessage: 'Amazing sunset in Bali! 🌅',
          location: 'Bali, Indonesia',
          country: 'Indonesia',
          coordinates: undefined,
          createdBy: userProfile?.uid || 'mock-user',
          recipientId: undefined,
          replyToId: undefined,
          createdAt: new Date(now - 2 * 24 * 60 * 60 * 1000),
          isRead: true,
          isPublic: true,
          tags: [],
          likes: 0,
          template: {
            id: 'classic',
            name: 'Classic',
            previewImage: '',
            borderStyle: 'classic',
            colorFilter: '',
            fontFamily: 'serif',
            textColor: '#000',
            backgroundColor: '#fff',
            animation: 'none',
          },
          emojiOverlays: [],
          isAnonymous: false,
          shareLink: undefined,
          translations: {},
          retryStatus: 'success',
          retryCount: 0,
          maxRetries: 5,
          lastRetryAttempt: undefined,
          retryErrorMessage: undefined,
        },
        {
          id: '2',
          imageURL: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=400',
          message: 'Exploring the streets of Tokyo',
          translatedMessage: 'Exploring the streets of Tokyo',
          location: 'Tokyo, Japan',
          country: 'Japan',
          coordinates: undefined,
          createdBy: userProfile?.uid || 'mock-user',
          recipientId: undefined,
          replyToId: undefined,
          createdAt: new Date(now - 5 * 24 * 60 * 60 * 1000),
          isRead: false,
          isPublic: true,
          tags: [],
          likes: 0,
          template: {
            id: 'modern',
            name: 'Modern',
            previewImage: '',
            borderStyle: 'modern',
            colorFilter: '',
            fontFamily: 'sans-serif',
            textColor: '#000',
            backgroundColor: '#fff',
            animation: 'none',
          },
          emojiOverlays: [],
          isAnonymous: false,
          shareLink: undefined,
          translations: {},
          retryStatus: 'success',
          retryCount: 0,
          maxRetries: 5,
          lastRetryAttempt: undefined,
          retryErrorMessage: undefined,
        },
      ];
      setRecentPostcards(mock);
      setLoading(false);
    }, 1000);
  }, [userProfile?.uid]);

  const stats = [
    {
      name: 'Postcards Sent',
      value: userProfile?.stats.postcardsSent || 0,
      icon: CameraIcon,
      color: 'text-primary-600',
      bgColor: 'bg-primary-100',
    },
    {
      name: 'Postcards Received',
      value: userProfile?.stats.postcardsReceived || 0,
      icon: InboxIcon,
      color: 'text-secondary-600',
      bgColor: 'bg-secondary-100',
    },
    {
      name: 'Countries Reached',
      value: userProfile?.stats.countriesReached || 0,
      icon: GlobeAltIcon,
      color: 'text-accent-600',
      bgColor: 'bg-accent-100',
    },
    {
      name: 'Total Points',
      value: userProfile?.stats.totalPoints || 0,
      icon: TrophyIcon,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
    },
  ];

  const quickActions = [
    {
      name: 'Create Postcard',
      description: 'Share a new travel postcard',
      href: '/create',
      icon: PlusCircleIcon,
      color: 'text-primary-600',
      bgColor: 'bg-primary-100',
    },
    {
      name: 'View Inbox',
      description: 'Check your received postcards',
      href: '/inbox',
      icon: InboxIcon,
      color: 'text-secondary-600',
      bgColor: 'bg-secondary-100',
    },
    {
      name: 'World Map',
      description: 'Explore your global journey',
      href: '/map',
      icon: MapIcon,
      color: 'text-accent-600',
      bgColor: 'bg-accent-100',
    },
    {
      name: 'Achievements',
      description: 'View your unlocked badges',
      href: '/achievements',
      icon: TrophyIcon,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-xl p-6 text-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-3xl font-bold mb-2">
            Welcome back, {userProfile?.displayName}! 👋
          </h1>
          <p className="text-primary-100">
            Ready to share your next adventure with the world?
          </p>
        </motion.div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className="card"
          >
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`h-6 w-6 ${stat.color}`} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action, index) => (
            <motion.div
              key={action.name}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <Link
                to={action.href}
                className="block p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all duration-200 group"
              >
                <div className={`p-3 rounded-lg ${action.bgColor} w-fit mb-3`}>
                  <action.icon className={`h-6 w-6 ${action.color}`} />
                </div>
                <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                  {action.name}
                </h3>
                <p className="text-sm text-gray-600 mt-1">{action.description}</p>
                <ArrowRightIcon className="h-4 w-4 text-gray-400 mt-2 group-hover:text-primary-600 transition-colors" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Recent Moments */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Recent Postcards</h2>
          <Link
            to="/inbox"
            className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center"
          >
            View all
            <ArrowRightIcon className="h-4 w-4 ml-1" />
          </Link>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : recentPostcards.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recentPostcards.map((postcard, index) => (
              <motion.div
                key={postcard.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="relative">
                  <img
                    src={postcard.imageURL}
                    alt="Postcard"
                    className="w-full h-48 object-cover"
                  />
                  {!postcard.isRead && (
                    <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                      New
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <p className="text-gray-900 font-medium mb-2">{postcard.message}</p>
                                     <div className="flex items-center justify-between text-sm text-gray-600">
                     <span>{postcard.location}</span>
                     <span>{(() => {
                       const date = convertToDate(postcard.createdAt);
                       return date ? date.toLocaleDateString() : 'Unknown';
                     })()}</span>
                   </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <InboxIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No postcards yet. Start sharing to see them here!</p>
            <Link
              to="/create"
              className="btn-primary mt-4 inline-flex items-center"
            >
              <PlusCircleIcon className="h-4 w-4 mr-2" />
              Create Your First Postcard
            </Link>
          </div>
        )}
      </div>

      {/* Achievement Preview */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Recent Achievements</h2>
          <Link
            to="/achievements"
            className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center"
          >
            View all
            <ArrowRightIcon className="h-4 w-4 ml-1" />
          </Link>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="bg-yellow-100 p-3 rounded-lg">
            <TrophyIcon className="h-6 w-6 text-yellow-600" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">First Postcard</h3>
            <p className="text-sm text-gray-600">You sent your first digital souvenir!</p>
          </div>
        </div>
      </div>
    </div>
  );
} 